/**
 * `IPCIServiceClient` — typed client for the PBC-operated PCI
 * service (substrate identity primitive).
 *
 * Activation composes an identity-proofing vendor attestation token
 * (NIST IAL2 per PFO §2.2) into substrate-issued patient identity
 * (PCI per Doctrine §6 Identity primitive + Tech Spec §4.1).
 * Wire shape (from `services/pci-service/src/app.ts` + `schemas.ts`):
 *
 *   POST /v1/pci    body: { identityToken: <vendor JWT> }
 *                   → 201 { issued: true, pci: { ... } }
 *                   → 200 { issued: false, pci: { ... } }   (already exists)
 *
 * The patient-facing rendering of the resulting PCI happens at the
 * Smart Health Account layer — the cleartext PCI is not surfaced as
 * a patient-facing concept in normal use (per Tech Spec §6.1
 * "Identity surface (substrate-mediated scope)"). Activation surfaces
 * the issued state ("your account is active") rather than the raw
 * identifier.
 *
 * SCAFFOLD: this module ships the consumer-side typed contract +
 * `MockPCIServiceClient` for tests + dev. Real HTTP wiring (mTLS,
 * retry, OTel span propagation, Authentication composing per
 * Tech Spec §4.1) lands at production-deployment scope which is
 * hire-gated. The mock satisfies the contract for activation flow
 * implementation in this PR.
 */
export class PCIServiceClientError extends Error {
    code;
    constructor(message, code) {
        super(message);
        this.name = 'PCIServiceClientError';
        this.code = code;
    }
}
// ---------- Mock implementation (test + dev) ----------
/**
 * In-memory mock for tests and dev environments. Configurable
 * outcome (success / token-invalid / service-unavailable) and
 * idempotency by identity token (re-presenting the same token
 * returns `issued: false`).
 */
export class MockPCIServiceClient {
    counter = 0;
    nextOutcome = 'success';
    issuedByToken = new Map();
    async issuePCI(req) {
        if (this.nextOutcome === 'identity-token-invalid') {
            throw new PCIServiceClientError('identity token rejected', 'identity-token-invalid');
        }
        if (this.nextOutcome === 'service-unavailable') {
            throw new PCIServiceClientError('PCI service unavailable', 'service-unavailable');
        }
        const existing = this.issuedByToken.get(req.identityToken);
        if (existing !== undefined) {
            return { ...existing, issued: false };
        }
        const pci = `pci_mock_${++this.counter}`;
        const record = {
            pci,
            issuedAt: new Date().toISOString(),
            issued: true,
        };
        this.issuedByToken.set(req.identityToken, record);
        return record;
    }
    /** Test-double helper: configure the next call's outcome. */
    seedNextOutcome(outcome) {
        this.nextOutcome = outcome;
    }
}
//# sourceMappingURL=pci-service-client.js.map